/**
 * Workbench 路由级权限表（UI 入口层守卫）
 *
 * 与 CRM 的 PERMISSIONS_CONFIG.navigation.path / protected-route-access 不同，
 * workbench 只维护自己页面的轻量路径策略：
 * - 学员可读页面（articles / student-tasks / review-course / sites / site-tools）不注册 → 默认放行
 * - 管理页面按需注册所需权限（OR 语义）
 * - /sio-tools/** 依赖页面内课程解锁门禁（capability 检查），路由层不重复
 *
 * 本表只做 UI 披露控制；每个 ServerFn handler 仍是权威授权点。
 */

import { P, type Permission } from "@crm/access/authorization/client";

export type WorkbenchRouteAccessRule = {
  /** 路径前缀匹配（无通配符；前缀 + 边界匹配） */
  readonly path: string;
  /** OR 语义：拥有任一权限即可进入 */
  readonly anyOf: readonly Permission[];
};

/** workbench 管理页路由权限表（未列出的路径默认放行，仅登录校验） */
export const WORKBENCH_ROUTE_ACCESS_RULES: readonly WorkbenchRouteAccessRule[] = [
  {
    path: "/student-feedback",
    anyOf: [P.CLASS_SESSION_FEEDBACK_READ_ALL],
  },
  // 知识库管理页：注意学员角色持有 knowledge-entry:read:all /
  // knowledge-tag:read:all（published 全员可读语义），因此这里只能要求
  // 管理/写权限——REVIEW/CLAIM/WRITE/PUBLISH 学员均无，会被挡。
  {
    path: "/knowledge/review",
    anyOf: [P.KNOWLEDGE_ENTRY_REVIEW_ALL],
  },
  {
    path: "/knowledge/pool",
    anyOf: [P.KNOWLEDGE_ENTRY_CLAIM_ALL, P.KNOWLEDGE_ENTRY_WRITE_ALL],
  },
  {
    path: "/knowledge/drafts",
    anyOf: [P.KNOWLEDGE_ENTRY_WRITE_ALL, P.KNOWLEDGE_ENTRY_WRITE_ASSIGNED],
  },
  {
    path: "/knowledge/tags",
    anyOf: [P.KNOWLEDGE_TAG_WRITE_ALL, P.KNOWLEDGE_TAG_DELETE_ALL],
  },
  {
    path: "/knowledge/edit",
    anyOf: [P.KNOWLEDGE_ENTRY_WRITE_ALL, P.KNOWLEDGE_ENTRY_WRITE_ASSIGNED],
  },
  // 课程管理页（/review-course 本身学员可进，admin 子页是教师管理界面）
  {
    path: "/review-course/admin",
    anyOf: [P.REVIEW_COURSE_WRITE_ALL],
  },
  // 设计模板管理页：学员有 design-template:read:all（Handler 内按 wp
  // enrollment 门禁），路由层要求写权限，学员被挡。
  // 本页包含写操作，不把只读教师权限混入；如需只读体验应建立独立路由。
  {
    path: "/design-templates",
    anyOf: [P.DESIGN_TEMPLATE_WRITE_ALL],
  },
];

/**
 * 匹配规则：路径等于规则 path，或以 `path/` 开头（边界匹配，避免
 * `/knowledge/review-x` 误命中 `/knowledge/review`）。
 */
export function matchWorkbenchRouteRule(routePath: string): WorkbenchRouteAccessRule | undefined {
  // 去除 query/hash 与尾部斜杠
  const normalized = routePath.split("?")[0]?.split("#")[0] ?? "";
  const path = normalized.replace(/\/+$/, "") || "/";
  return WORKBENCH_ROUTE_ACCESS_RULES.find((rule) => {
    if (path === rule.path) return true;
    return path.startsWith(`${rule.path}/`);
  });
}

/**
 * 判定当前用户对 routePath 是否有 UI 进入权限。
 * 返回 true 表示放行（含未注册路径的默认放行）。
 */
export function hasWorkbenchRoutePermission(
  userPermissions: ReadonlySet<Permission>,
  routePath: string,
): boolean {
  const rule = matchWorkbenchRouteRule(routePath);
  if (!rule) {
    return true;
  }
  return rule.anyOf.some((perm) => userPermissions.has(perm));
}
